/**
 * Invitation helpers shared by the Users list, User detail/edit and the Invitations list.
 */

import { InvitationStatus } from '@/types/users'

type InvitationLike = {
  status: string
  expiresAt: number // epoch seconds
  emailId: string
  acceptedUserNumber?: number | null
  createdAt?: number
}

/**
 * Status to show the inviter. Expiry is derived, not stored (backend O7): an overdue invitation
 * stays `pending` server-side forever, so display it as `expired` — but Resend/Revoke still work.
 */
export function invitationDisplayStatus(inv: InvitationLike, nowMs = Date.now()): InvitationStatus {
  if (inv.status === InvitationStatus.Pending && inv.expiresAt * 1000 < nowMs) return InvitationStatus.Expired
  return inv.status as InvitationStatus
}

/** Resend / revoke / edit are only allowed while the backend still has the row as `pending`. */
export const isInvitationActionable = (inv: Pick<InvitationLike, 'status'>) => inv.status === InvitationStatus.Pending

/**
 * The most recent invitation for a user: match on acceptedUserNumber when the invitation has one,
 * else on email (case-insensitive). A person can hold several invitations; newest wins.
 */
export function findUserInvitation<T extends InvitationLike>(
  invitations: T[],
  user: { userNumber?: number; emailAddress?: string | null },
): T | undefined {
  const email = (user.emailAddress ?? '').trim().toLowerCase()
  return invitations
    .filter((inv) =>
      inv.acceptedUserNumber != null
        ? user.userNumber != null && Number(inv.acceptedUserNumber) === Number(user.userNumber)
        : !!email && inv.emailId.trim().toLowerCase() === email,
    )
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0]
}
